// Printer setup — Feb 2026 rewrite.
//
// Single supported path: a standalone APK ("جسر طابعة طلبية") that runs
// a localhost HTTP server on 127.0.0.1:8899 and talks ESC/POS to the
// Bluetooth MAC address the operator entered inside the APK.
//
// This page is a thin monitor of the bridge state + a helper to push
// the MAC address to the APK over POST /mac (so the operator can also
// configure it from the browser without opening the APK).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Bluetooth, Printer, PrinterCheck, Wifi, WifiOff, RefreshCw, Save } from "lucide-react";
import { refreshNyxBridge, getBridgeStatus, isNb55BridgeAvailable, nb55PrintText } from "@/utils/nb55Printer";

const BRIDGE_URL = "http://127.0.0.1:8899";
const MAC_REGEX = /^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){5}$/;

export default function PrinterSetup() {
  const [status, setStatus] = useState(null);
  const [mac, setMac] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = async () => {
    setErr("");
    await refreshNyxBridge();
    const s = await getBridgeStatus();
    setStatus(s);
    if (s?.mac && !mac) setMac(s.mac);
  };

  useEffect(() => { refresh(); const t = setInterval(refresh, 10_000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bridgeOk = !!status?.ok;
  const printerConnected = !!status?.bound;

  const saveMac = async () => {
    const value = mac.trim().toUpperCase();
    if (!MAC_REGEX.test(value)) {
      setErr("صيغة MAC غير صحيحة. مثال صحيح: AA:BB:CC:DD:EE:FF");
      return;
    }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${BRIDGE_URL}/mac`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mac: value }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("✓ تم حفظ عنوان MAC في الجسر", { duration: 2500 });
      await refresh();
    } catch (e) {
      setErr("تعذّر إرسال MAC إلى الجسر: " + (e?.message || e) + " — تأكد أن تطبيق الجسر مفتوح.");
    } finally {
      setBusy(false);
    }
  };

  const testPrint = async () => {
    if (!isNb55BridgeAvailable()) {
      setErr("جسر الطابعة غير متصل — افتح تطبيق \"جسر طابعة طلبية\" على جهاز POS.");
      return;
    }
    const text =
      "========================\n" +
      "     طلبية Talabya\n" +
      "========================\n" +
      "    اختبار الطباعة\n" +
      "   الطابعة تعمل ✓\n" +
      "------------------------\n" +
      `  ${new Date().toLocaleString("ar-LY")}\n` +
      "========================\n";
    const ok = nb55PrintText(text);
    if (ok) toast.success("🖨️ تم إرسال الطباعة", { duration: 2500 });
    else setErr("فشل إرسال الطباعة.");
  };

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="printer-setup-page">
      <TopBar/>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-5">
          <BackButton to="/restaurant" variant="pill"/>
          <div className="flex-1">
            <h1 className="text-2xl font-black flex items-center gap-2" style={{fontFamily:"Cairo"}}>
              <Bluetooth className="text-primary-brand" size={22}/> إعداد الطابعة الحرارية
            </h1>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              جسر بلوتوث مستقل · إدخال MAC Address يدوياً
            </p>
          </div>
          <button onClick={refresh} disabled={busy}
            className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] text-xs font-black flex items-center gap-1 hover:bg-[var(--surface-alt)]"
            data-testid="btn-refresh">
            <RefreshCw size={14}/> تحديث
          </button>
        </div>

        {/* Bridge status card */}
        <div className={`rounded-3xl p-5 talabya-shadow-soft mb-4 border-2 ${bridgeOk ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}
             data-testid="bridge-status">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {bridgeOk ? <Wifi className="text-emerald-600" size={28}/> : <WifiOff className="text-red-600" size={28}/>}
              <div>
                <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>
                  {bridgeOk ? "جسر الطابعة متصل" : "جسر الطابعة غير متصل"}
                </div>
                <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                  127.0.0.1:8899 {status?.version ? `· v${status.version}` : ""}
                </div>
              </div>
            </div>
            <div className={`shrink-0 w-3 h-3 rounded-full ${bridgeOk ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`}/>
          </div>

          {!bridgeOk && (
            <div className="mt-3 text-xs text-red-800 bg-white/70 rounded-xl p-3 leading-relaxed space-y-1.5">
              <div>افتح تطبيق <b>جسر طابعة طلبية</b> على جهاز POS الخاص بك.</div>
              {status?.error && (
                <div className="font-mono text-[10px] bg-red-100 border border-red-200 rounded p-2 text-red-900" dir="ltr">
                  <b>تفاصيل الخطأ:</b> {status.error}
                </div>
              )}
              <div className="text-[11px] text-[var(--text-secondary)]">
                إن ظهر خطأ <code>Failed to fetch</code> فأعِد تثبيت أحدث نسخة APK (v2.1.0+) — تضم إصلاح <code>Private Network Access</code>.
              </div>
            </div>
          )}

          {bridgeOk && (
            <div className="mt-3 flex items-center justify-between bg-white/70 rounded-xl p-3">
              <div className="flex items-center gap-2 text-sm">
                {printerConnected ? (
                  <><PrinterCheck size={16} className="text-emerald-600"/>
                    <span className="font-black">متصلة بالطابعة</span>
                    <span className="font-mono text-xs text-[var(--text-secondary)]" dir="ltr">{status?.mac}</span>
                  </>
                ) : (
                  <><Printer size={16} className="text-amber-600"/>
                    <span className="font-black text-amber-800">غير متصلة بالطابعة بعد</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {err && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm" data-testid="setup-err">
            {err}
          </div>
        )}

        {/* MAC configuration */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Bluetooth size={18} className="text-primary-brand"/>
            <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>عنوان MAC للطابعة</h2>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-3 leading-relaxed">
            أدخل عنوان MAC للطابعة الحرارية الخاصة بك (موجود في إعدادات
            البلوتوث على جهاز POS، أسفل اسم الطابعة المُقرنة).
          </p>

          <input
            type="text"
            value={mac}
            onChange={(e) => setMac(e.target.value.toUpperCase())}
            placeholder="AA:BB:CC:DD:EE:FF"
            dir="ltr"
            maxLength={17}
            className="w-full px-4 py-3 rounded-xl border-2 border-[var(--border)] bg-[var(--surface-alt)] font-mono text-center text-lg tracking-wider focus:border-primary-brand focus:bg-white"
            data-testid="mac-input"
          />

          <button
            onClick={saveMac}
            disabled={busy || !bridgeOk || !mac}
            className="w-full mt-3 py-3 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="btn-save-mac">
            <Save size={16}/> حفظ وربط
          </button>
        </div>

        {/* Test print */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5">
          <button
            onClick={testPrint}
            disabled={!bridgeOk}
            className="w-full py-3 rounded-xl bg-emerald-600 text-white font-black text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="btn-test-print">
            <Printer size={16}/> طباعة تجريبية
          </button>
          <p className="text-[11px] text-[var(--text-secondary)] mt-2 text-center">
            ترسل صفحة اختبار قصيرة إلى الطابعة عبر الجسر
          </p>
        </div>

        <div className="mt-6 text-xs text-[var(--text-secondary)] bg-white border border-[var(--border)] rounded-2xl p-4 leading-relaxed">
          <b className="block mb-1 text-[var(--text-primary)]">كيف أحصل على MAC الطابعة؟</b>
          افتح إعدادات البلوتوث على جهاز POS الخاص بك، قم بإقران الطابعة
          مرة واحدة (إن لم تكن مقرنة). ثم اضغط على اسم الطابعة في قائمة
          الأجهزة المقرنة لتظهر لك معلوماتها بما فيها عنوان MAC.
        </div>
      </div>
    </div>
  );
}
